/**
 * @fileoverview Remembers the profile photo of a user who signed in with
 * Google, so the navbar can show it in place of the generic user icon.
 *
 * The backend verifies the Google ID token but does not return its `picture`
 * claim, so the photo is read from the token here. It is display-only: whether
 * the user may sign in is decided by the backend, and the photo is stored only
 * after that sign-in succeeds. Like the other pure helpers, it takes its
 * storage as a dependency (no DOM/Angular), so it unit-tests deterministically.
 */

/** The slice of `Storage` this needs; `localStorage` satisfies it. */
export type PhotoStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The photo and the account it belongs to, so another user never sees it. */
export interface GooglePhoto {
  email: string;
  url: string;
}

const STORAGE_KEY = 'google_profile_photo';

/**
 * Reads the account email and photo URL from a Google ID token's payload.
 * The signature is not checked; the backend already has.
 *
 * @param credential The ID token (a JWT) handed over by Google Identity Services.
 * @return The photo, or null when the token has no usable `https` picture.
 */
export function photoFromCredential(credential: string): GooglePhoto | null {
  try {
    const payload = credential.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes));
    const { email, picture } = claims ?? {};
    if (typeof email !== 'string' || typeof picture !== 'string') return null;
    if (!picture.startsWith('https://')) return null;
    return { email: email.toLowerCase(), url: picture };
  } catch {
    return null;
  }
}

export class GoogleProfilePhoto {
  constructor(private readonly storage: PhotoStorage) {}

  /** Stores the photo from a successful Google sign-in, replacing any earlier one. */
  remember(credential: string): void {
    const photo = photoFromCredential(credential);
    if (!photo) {
      this.forget();
      return;
    }
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(photo));
    } catch {
      // Storage full or blocked: the navbar falls back to the user icon.
    }
  }

  /** The stored photo URL when it belongs to `email`, otherwise null. */
  photoFor(email: string | null | undefined): string | null {
    if (!email) return null;
    try {
      const photo: Partial<GooglePhoto> | null = JSON.parse(
        this.storage.getItem(STORAGE_KEY) ?? 'null',
      );
      return photo?.email === email.toLowerCase() && typeof photo.url === 'string'
        ? photo.url
        : null;
    } catch {
      return null;
    }
  }

  /** Drops the stored photo, e.g. on sign-out or a password sign-in. */
  forget(): void {
    try {
      this.storage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to clear when storage is unavailable.
    }
  }
}
